/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from "three";

import { const_var, params } from '../../../../../redux/reducers/reducer';
import { delModel, extrudeSettings, railModel } from '../../../../../utils/Functions';
import { CSG } from '../../../../../assets/libs/csg-lib';
import PillarRailModel from '../../Legs';
import BaseRailModel from '../../BaseRail';

const QCT = ({ normalMap, envMap, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr }) => {
    let meshDelModel_CSG = {};
    const wallValueArr = ['front', 'back', 'left', 'right', 'backStorageFront', 'leftStorageFront', 'rightStorageFront'];

    wallValueArr.forEach((item) => {
        const meshDelModel = new THREE.Mesh(
            new THREE.ExtrudeGeometry(delModel(item, 'leg'), extrudeSettings(3)),
            new THREE.MeshStandardMaterial({ color: 'red' })
        )
        const meshDelModel_Temp = meshDelModel.clone();
        if (item === 'front' || item === 'back'|| item === 'backStorageFront') {
            meshDelModel_Temp.position.set(0, 0, -1.5)
        } else {
            meshDelModel_Temp.rotateY(-Math.PI / 2)
            meshDelModel_Temp.position.set(1.5, 0, 0)
        }
        meshDelModel_Temp.updateMatrix();
        meshDelModel_Temp.updateMatrixWorld();

        const mesh_CSG = CSG.fromMesh(meshDelModel_Temp);

        meshDelModel_CSG = { ...meshDelModel_CSG,  [item]: mesh_CSG };
    })

    return (
        <>
            <BaseRailModel normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} />
            <PillarRailModel trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} />
            <TrussModel trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} />
        </>
    )
}

export default QCT

const TrussModel = ({ trussModelPosInfoArr, normalMap, envMap, overlapTrussArr, hiddenBaseRailIdArr }) => {
    const obj_upperTrussNormal = new THREE.Object3D();
    const obj_upperTrussHighlight = new THREE.Object3D();
    const obj_centerVRail = new THREE.Object3D();
    const obj_centerHRail = new THREE.Object3D();
    const obj_connectVRail = new THREE.Object3D();
    const obj_connectSRail = new THREE.Object3D();
    const obj_underTruss = new THREE.Object3D();

    const ref_upperTrussNormal = useRef();
    const ref_centerVRail = useRef();
    const ref_centerHRail = useRef();
    const ref_connectVRail = useRef();
    const ref_connectSRail  = useRef();
    const ref_underTruss = useRef();

    const bottomBaseRailWidth = 0.3;
    const bottomBaseRailHeight = 0.1;
    const connectRailWidth = 0.3;
    const connectRailHeight = 0.2;
    const offset = 0.035;
    const roofRailDistanceY = 1;

    const alignPosOffset = useMemo(() => {
        if(params.p_r_s === 1 ) return 0.35 + (params.p_r_p - 3) * (0.02) ;
        else if(params.p_r_s === 2 || params.p_r_s === 3) return 0 - offset;
        else return 0 - offset;
    }, [params.p_r_s, params.p_r_p])
    
    const roofAlpha = Math.atan(params.p_r_p / 12);
    const roofHeight = (params.p_w / 2) * (params.p_r_p / 12);
    const roofOuterRailLength = useMemo(() => (Math.sqrt(Math.pow(params.p_w / 2, 2) + Math.pow(roofHeight, 2))), [roofHeight, params.p_w]) 
    const bottomBaseRailLength = (params.p_w - bottomBaseRailHeight / 2 - connectRailWidth / 2) / 2;
    const roofCenterRailHeight = ((params.p_w / 2) * params.p_r_p / 12) / 2 + roofRailDistanceY;
    
    const connectRailDataArr = useMemo(() => {
        let dataInfoArray = [];
        let count, distance;
        let slopeRailLength = 0;

        let initDistance = 3;
        const tempCount = Math.floor((params.p_w / 2) / initDistance);
        if ((params.p_w / 2) === tempCount * initDistance) {
            count = tempCount;
            distance = initDistance;
        } else if ((params.p_w / 2) - tempCount * initDistance <= initDistance / 2) {
            count = tempCount;
            distance = (params.p_w / 2) / count;
        } else {
            count = tempCount + 1;
            distance = (params.p_w / 2) / count;
        }

        for (let i = 1; i <= count; i++) {
            const pos_x = distance * i - connectRailWidth / 2;
            const pos_y = params.p_h + (params.p_w / 2 - pos_x) * (params.p_r_p / 12) - roofRailDistanceY + 0.03;
            slopeRailLength = Math.sqrt(Math.pow(distance, 2) + Math.pow((roofRailDistanceY + distance * Math.tan(roofAlpha)), 2)) - 0.05;
            const alpha = Math.asin(distance / slopeRailLength);
            const verticalRailLength = roofRailDistanceY;
            
            const tempInfoObj = {
                distance: distance,
                pos_x: pos_x,
                pos_y: pos_y,
                s_length: slopeRailLength,
                v_length: verticalRailLength,
                alpha: alpha
            }

            dataInfoArray.push(tempInfoObj);
        }

        return {
            slopeRailLength,
            dataInfoArray
        }
    }, [params.p_h, params.p_w, roofAlpha, params.p_r_p])

    useEffect(() => {
        if (ref_upperTrussNormal.current && ref_centerHRail.current && ref_centerVRail.current && ref_connectVRail.current && ref_connectSRail.current && ref_underTruss) {
            let index_all_1 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 1) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    //----- roof upper truss -----
                    obj_upperTrussNormal.position.set(offsetForDir * (params.p_w / 2 - connectRailWidth / 4), params.p_h - connectRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_upperTrussNormal.rotation.y = offsetForDir * -Math.PI / 2;
                    obj_upperTrussNormal.rotateOnAxis(new THREE.Vector3(1, 0, 0), offsetForDir * roofAlpha);

                    //----- roof under rail-----
                    obj_underTruss.position.set(offsetForDir * (-params.p_w / 2 + connectRailWidth / 4), params.p_h - connectRailHeight / 2 - roofRailDistanceY + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_underTruss.rotation.y = offsetForDir * Math.PI / 2;
                    obj_underTruss.rotateOnAxis(new THREE.Vector3(1, 0, 0), offsetForDir * roofAlpha);
                    //--------------------------

                    //----- bottom base rail-----
                    obj_centerHRail.position.set(-bottomBaseRailLength / 2, params.p_h + roofHeight / 2 - roofRailDistanceY - bottomBaseRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerHRail.rotation.y = Math.PI / 2;
                    //---------------------

                    //----- roof center rail-----
                    obj_centerVRail.position.set(0, params.p_h + roofHeight / 2 - roofRailDistanceY - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerVRail.rotation.x = -Math.PI / 2;

                    //---------------------

                    if (!!overlapTrussArr && overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index_truss)) {
                        obj_upperTrussNormal.scale.setScalar(0);
                    } else {
                        obj_upperTrussNormal.scale.setScalar(1);
                    }

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_underTruss.scale.setScalar(0);
                        obj_centerHRail.scale.setScalar(0);
                        obj_centerVRail.scale.setScalar(0);
                    } else {
                        obj_underTruss.scale.setScalar(1);
                        obj_centerHRail.scale.setScalar(1);
                        obj_centerVRail.scale.setScalar(1);
                    }

                    obj_upperTrussNormal.updateMatrix();
                    ref_upperTrussNormal.current.setMatrixAt(index_all_1, obj_upperTrussNormal.matrix);
                    obj_underTruss.updateMatrix();
                    ref_underTruss.current.setMatrixAt(index_all_1, obj_underTruss.matrix);
                    obj_centerVRail.updateMatrix();
                    ref_centerVRail.current.setMatrixAt(index_all_1, obj_centerVRail.matrix);
                    obj_centerHRail.updateMatrix();
                    ref_centerHRail.current.setMatrixAt(index_all_1, obj_centerHRail.matrix);

                    index_all_1 ++;
                })
            })

            let index_all_2 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectRailDataArr.dataInfoArray.length).fill('').forEach((_, index_rail) => {
                        obj_connectVRail.position.set(offsetForDir * connectRailDataArr.dataInfoArray[index_rail].pos_x, connectRailDataArr.dataInfoArray[index_rail].pos_y - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_connectVRail.rotation.x = -Math.PI / 2;

                        obj_connectSRail.position.set(offsetForDir * connectRailDataArr.dataInfoArray[index_rail].pos_x, connectRailDataArr.dataInfoArray[index_rail].pos_y - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_connectSRail.rotation.y = offsetForDir * Math.PI / 2;
                        obj_connectSRail.rotateOnAxis(new THREE.Vector3(1, 0, 0), offsetForDir * (-Math.PI / 2 - connectRailDataArr.dataInfoArray[index_rail].alpha));

                        if (hiddenBaseRailIdArr.includes(index_truss)) {
                            obj_connectVRail.scale.setScalar(0);
                            obj_connectSRail.scale.setScalar(0);
                        } else {
                            obj_connectVRail.scale.setScalar(1);
                            obj_connectSRail.scale.setScalar(1);
                        }

                        obj_connectVRail.updateMatrix();
                        ref_connectVRail.current.setMatrixAt(index_all_2, obj_connectVRail.matrix);
                        obj_connectSRail.updateMatrix();
                        ref_connectSRail.current.setMatrixAt(index_all_2, obj_connectSRail.matrix);

                        index_all_2 ++;
                    })
                })
            })
            
            ref_upperTrussNormal.current.instanceMatrix.needsUpdate = true;
            ref_centerHRail.current.instanceMatrix.needsUpdate = true;
            ref_centerVRail.current.instanceMatrix.needsUpdate = true;
            ref_connectVRail.current.instanceMatrix.needsUpdate = true;
            ref_connectSRail.current.instanceMatrix.needsUpdate = true;
            ref_underTruss.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_upperTrussNormal.current, ref_centerHRail.current, ref_centerVRail.current, ref_connectVRail.current, ref_connectSRail.current, ref_underTruss, params.p_w, params.p_h, params.singleSlope, params.freeStandingSide, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, connectRailDataArr, alignPosOffset, roofAlpha])

    //---highlight roof truss
    const ref_upperTrussHighlight = useMemo(() => {
        if (overlapTrussArr.idArr?.length > 0) {
            const instanceMat = [];
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 1) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    obj_upperTrussHighlight.position.set(offsetForDir * (params.p_w / 2 - connectRailWidth / 4), params.p_h - connectRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_upperTrussHighlight.rotation.y = offsetForDir * -Math.PI / 2;
                    obj_upperTrussHighlight.rotateOnAxis(new THREE.Vector3(1, 0, 0), offsetForDir * roofAlpha);

                    if (overlapTrussArr.buildingType === 'center' && overlapTrussArr.idArr.includes(index_truss)) {
                        obj_upperTrussHighlight.scale.setScalar(1);
                    } else {
                        obj_upperTrussHighlight.scale.setScalar(0);
                    }

                    obj_upperTrussHighlight.updateMatrix();
                    instanceMat.push(...obj_upperTrussHighlight.matrix.elements);
                })
            })
            
            return new THREE.InstancedBufferAttribute(new Float32Array(instanceMat), 16);
        }
    }, [overlapTrussArr.idArr, params.p_w, params.p_h, params.singleSlope, params.freeStandingSide, trussModelPosInfoArr, overlapTrussArr, alignPosOffset, roofAlpha])

    return (
        <group position={[0, -offset, 0]} name='QCT-truss-legs'>
          <>
                {/* --- roof unnder truss model --- */}
                <instancedMesh ref={ref_underTruss} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(roofOuterRailLength - 0.04)]} />
                    <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- roof upper truss model --- */}
                <instancedMesh ref={ref_upperTrussNormal} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(roofOuterRailLength - (0.05 - (parseFloat(params.p_r_p) - 3) * 0.017 / 3))]} />
                    <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- bottom base rail model --- */}
                <instancedMesh   instancedMesh ref={ref_centerHRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(bottomBaseRailWidth, bottomBaseRailHeight), extrudeSettings(bottomBaseRailLength)]} />
                    <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- roof center rail model --- */}
                <instancedMesh ref={ref_centerVRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofCenterRailHeight)]} />
                    <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- connect rail model --- */}
                <instancedMesh ref={ref_connectVRail} args={[null, null, trussModelPosInfoArr.length * connectRailDataArr.dataInfoArray.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofRailDistanceY)]} />
                    <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
                </instancedMesh>
                <instancedMesh ref={ref_connectSRail} args={[null, null, trussModelPosInfoArr.length * connectRailDataArr.dataInfoArray.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(connectRailDataArr.slopeRailLength - 0.15)]} />
                    <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- roof highlight truss model --- */}
                {
                    overlapTrussArr.idArr?.length > 0 && <instancedMesh instanceMatrix-needsUpdate={true} instanceMatrix={ref_upperTrussHighlight} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                        <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(roofOuterRailLength - (0.05 - (parseFloat(params.p_r_p) - 3) * 0.017 / 3))]} />
                        <meshBasicMaterial depthTest={false} color={'#900000'} opacity={0.7} transparent />
                    </instancedMesh>
                }
                {/* ------------------------- */}

               
            </>
           
        </group>
    )
}